/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {PixiesVsPiratesGameConfig} from "../../PixiesVsPiratesGameConfig";

export class PixiesVsPiratesBigWinView extends BaseView {

    private _layer:ScalableLayer;
    private _dimmer:PIXI.Graphics;
    private _bigWinTextLoop:TimelineSprite;
    private _megaWinTextLoop:TimelineSprite;
    private _bigWinText:MultiSprites;
    private _superText:TimelineSprite;
    private _bigWinPoof:TimelineSprite;
    private _isShowing:boolean = false;

    constructor() {
        super();
        this._layer = StageManager.getLayer(PixiesVsPiratesGameConfig.instance.LAYERS.BIG_WIN.name);
    }

    protected initAnimations():void {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);

        this._bigWinTextLoop = this.createTimeLineSprite(GameResources.getTextures("bigWinLoop"), 4, [GameConfig.instance.GAME_WIDTH / 2, GameConfig.instance.GAME_HEIGHT / 2], 30, true);
        this._megaWinTextLoop = this.createTimeLineSprite(GameResources.getTextures("megaWinLoop"), 4, [GameConfig.instance.GAME_WIDTH / 2, GameConfig.instance.GAME_HEIGHT / 2], 30, true);

        this._bigWinText = new MultiSprites({
            bigWinText : {
                textures : GameResources.getTextures("bigWinText"),
                anchor : [0.5, 0.5]
            },
            megaWinText : {
                textures : GameResources.getTextures("megaWinText"),
                anchor : [0.5, 0.5]
            }
        }, 15);
        this._bigWinText.visible = false;
        this._layer.addChild(this._bigWinText);

        const textPositionX:number = MathHelper.roundToDecimals(GameConfig.instance.GAME_WIDTH / 2, 0);
        const textPositionY:number = MathHelper.roundToDecimals(GameConfig.instance.GAME_HEIGHT / 3, 0);
        this._bigWinText.x = textPositionX;
        this._bigWinText.y = textPositionY;

        this._superText = this.createTimeLineSprite(GameResources.getTextures("superMegaWinText"), 1, [textPositionX, textPositionY - 100], 15);
        this._bigWinPoof = this.createTimeLineSprite(GameResources.getTextures("bigWinPoof"), 4, [GameConfig.instance.GAME_WIDTH / 2, GameConfig.instance.GAME_HEIGHT / 2], 30, true);
        StageManager.getLayer(PixiesVsPiratesGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._bigWinPoof);
    }

    private createTimeLineSprite(textures:PIXI.Texture[], scale:number, position:[number, number], fps:number = 30, isAddMode:boolean = false):TimelineSprite {
        const sprite:TimelineSprite = new TimelineSprite(textures, fps);
        sprite.anchor.set(0.5);
        sprite.scale.set(scale);
        sprite.alpha = 0;
        sprite.visible = false;
        sprite.position.set(position[0], position[1]);
        if(isAddMode) {
            sprite.blendMode = PIXI.BLEND_MODES.ADD;
        }
        this._layer.addChild(sprite);
        return sprite;
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.8);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);
    }

    public getBigWinLevelAnimation(winRatioConfig:IWinRatioConfig):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            this.updateBigWinLevelAnimation(winRatioConfig, true),
            this.updateBigWinLoop(winRatioConfig.type!),
            this._bigWinPoof.getAnimationAutoShowHide(),
            TweenLite.to(this._bigWinPoof, 0.2, {alpha : 1}),
            TweenLite.to(this._dimmer, 0.2, {alpha : 1}),
            TweenLite.to(this._bigWinText.scale, winRatioConfig.duration, {x : 1})
        ]);
        return timeline;
    }

    private updateBigWinLevelAnimation(winRatioConfig:IWinRatioConfig, animate:boolean):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();

        if(winRatioConfig.type === WinRatioType.SUPER_MEGA_WIN) {
            timeline.add([
                () => this._superText.visible = true,
                TweenLite.to(this._superText, animate ? 0.3 : 0.01, {alpha : 1}),
                this._superText.getAnimation(animate ? [0] : [-1, -1])
            ], 0);
            animate = false;
        }

        timeline.add([
            () => this._bigWinText.visible = true,
            this._bigWinText.playSpriteFrames(winRatioConfig.text!,
                {
                    duration : animate ? 0.3 : 0.01,
                    vars : animate ? [{fromVars : {alpha : 0}, toVars : {alpha : 1}}] : [{toVars : {alpha : 1}}]
                },
                {
                    duration : animate ? 0.2 : 0.01,
                    vars : [{toVars : {alpha : 0}}]
                },
                animate ? [0] : [-1, -1])
        ], 0);
        return timeline
    }

    private updateBigWinLoop(type:WinRatioType):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        if(type != WinRatioType.BIG_WIN) {
            timeline.add(TweenLite.to(this._bigWinTextLoop, 0.2, {
                alpha : 0,
                onComplete : () => {
                    this._bigWinTextLoop.visible = false;
                    this._bigWinTextLoop.stopLoop();
                }
            }), 0);
        }
        const newLoop:TimelineSprite = type === WinRatioType.BIG_WIN ? this._bigWinTextLoop : this._megaWinTextLoop;
        timeline.add(TweenLite.to(newLoop, 0.3, {
            alpha : 1,
            onStart : () => {
                newLoop.playLoop();
                newLoop.visible = true;
            }
        }), 0);
        return timeline;
    }

    public playEndingAnimation(endWinRatioConfig:IWinRatioConfig):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            this.updateBigWinLevelAnimation(endWinRatioConfig, false),
            TweenLite.to(this._dimmer, 0.1, {alpha : 0}),
            TweenLite.to(this._bigWinTextLoop, 0.2, {
                alpha : 0,
                onComplete : () => {
                    this._bigWinTextLoop.visible = false;
                    this._bigWinTextLoop.stopLoop();
                }
            }),
            TweenLite.to(this._megaWinTextLoop, 0.2, {
                alpha : 0,
                onComplete : () => {
                    this._megaWinTextLoop.visible = false;
                    this._megaWinTextLoop.stopLoop();
                }
            }),
            TweenLite.to(this._bigWinPoof, 0.2, {
                alpha : 0,
                onComplete : () => this._bigWinPoof.visible = false
            })
        ]);
        timeline.add([
            this._bigWinText.playSprite(endWinRatioConfig.text!, {
                duration : 0.2,
                vars : [{toVars : {alpha : 0}}]
            }),
            TweenLite.to(this._superText, 0.2, {alpha : 0})
        ], 0.5);
        timeline.add(() => {
            this._isShowing = false;
            this._bigWinText.visible = false;
            this._superText.visible = false;
        });

        timeline.play();
    }
}